import { useEffect, useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

/**
 * CreatorHubBar — thin, full-width announcement bar that sits ABOVE the
 * tenant ticker + nav.
 *
 * Fleet-wide standard: the affiliate link is NEVER hardcoded. It is pulled
 * dynamically from the EIGOO Affiliate Hub (`partner_affiliates` collection)
 * via GET /api/public/creator-hub-link. If no link is registered yet the bar
 * stays hidden entirely — never shown with a broken or placeholder URL.
 *
 *   204 No Content   → affiliate unregistered → bar hidden
 *   200 { link, slug, display_name } → bar renders, click opens the share link
 *
 * No manual env-var fallback. The Affiliate Hub is the single source of truth.
 */
export default function CreatorHubBar() {
    const [state, setState] = useState({ loading: true, link: null });

    useEffect(() => {
        let cancelled = false;
        api.get("/public/creator-hub-link")
            .then(({ data, status }) => {
                if (cancelled) return;
                if (status === 204 || !data || !data.link) {
                    setState({ loading: false, link: null });
                } else {
                    setState({
                        loading: false,
                        link: data.link,
                        slug: data.slug,
                        displayName: data.display_name,
                    });
                }
            })
            .catch(() => {
                if (!cancelled) setState({ loading: false, link: null });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Hide while loading (prevents flicker) AND when no link is registered.
    if (state.loading || !state.link) return null;

    return (
        <a
            href={state.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="creator-hub-bar"
            data-affiliate-slug={state.slug}
            className="group block w-full border-b transition-colors"
            style={{
                background: "linear-gradient(90deg, #f0c150 0%, #d4a04b 100%)",
                color: "#0a0e17",
                borderColor: "rgba(10,14,23,0.2)",
            }}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 py-2 flex items-center justify-center gap-2 text-center">
                <Sparkles size={12} className="shrink-0 opacity-80" aria-hidden />
                <span className="text-[0.68rem] md:text-xs tracking-[0.14em] uppercase font-semibold">
                    Join our growing creator hub —{" "}
                    <span className="underline underline-offset-4 decoration-dotted">
                        become an affiliate partner
                    </span>
                </span>
                <ArrowRight
                    size={12}
                    className="shrink-0 opacity-70 group-hover:translate-x-0.5 transition-transform"
                    aria-hidden
                />
            </div>
        </a>
    );
}
